import React, { useState } from 'react';
import { useLocale } from '../../context/LocaleContext';
import { getDeviceInfo } from '../../utils/deviceDetection';
import { apiService } from '../../services/api';
import styles from '../../styles/workshop2026Page.module.css';

const WORKSHOP_QUESTION_TYPE = '2026 Workshop Question';
const WORKSHOP_LOCATION = '2026 SAA Step Workshop';

interface WorkshopFaqInlineAskProps {
  isSignedIn: boolean;
  onLoginForQuestion: () => void;
  userEmail?: string;
  userDisplayName?: string;
}

const WorkshopFaqInlineAsk: React.FC<WorkshopFaqInlineAskProps> = ({
  isSignedIn,
  onLoginForQuestion,
  userEmail,
  userDisplayName,
}) => {
  const { t } = useLocale();
  const [questionText, setQuestionText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  if (!isSignedIn) {
    return (
      <div className={styles.faqAskBlock}>
        <p className={styles.faqAnswerText}>{t('workshop.2026.faq.ask_signin')}</p>
        <button type="button" className={styles.primaryBtn} onClick={onLoginForQuestion}>
          {t('workshop.2026.faq.ask_signin_cta')}
        </button>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!questionText.trim() || !userEmail) return;

    setIsSubmitting(true);
    setSubmitError(null);

    const askerLabel = userDisplayName?.trim()
      ? `${userDisplayName.trim()} <${userEmail}>`
      : userEmail;
    const feedbackText = `From: ${askerLabel}\n\nQuestion:\n${questionText.trim()}`;

    try {
      await apiService.submitFeedback({
        userEmail,
        locationInApp: WORKSHOP_LOCATION,
        feedbackType: WORKSHOP_QUESTION_TYPE,
        feedbackText,
        deviceInfo: getDeviceInfo(),
        emailCopyToUser: false,
      });
      setSubmitSuccess(true);
      setQuestionText('');
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : t('workshop.2026.faq.ask_error')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className={styles.faqAskBlock}>
        <p className={styles.faqAskSuccess}>{t('workshop.2026.faq.ask_thanks')}</p>
        <button
          type="button"
          className={styles.textButton}
          onClick={() => setSubmitSuccess(false)}
        >
          {t('workshop.2026.faq.ask_another')}
        </button>
      </div>
    );
  }

  return (
    <form className={styles.faqAskBlock} onSubmit={handleSubmit}>
      <label className={styles.faqAskLabel} htmlFor="faqQuestionText">
        {t('workshop.2026.faq.ask_label')}
      </label>
      <textarea
        id="faqQuestionText"
        className={styles.faqAskTextarea}
        value={questionText}
        onChange={(e) => setQuestionText(e.target.value)}
        placeholder={t('workshop.2026.faq.ask_placeholder')}
        rows={4}
        required
      />
      {submitError && <p className={styles.faqAskError}>{submitError}</p>}
      <button
        type="submit"
        className={styles.primaryBtn}
        disabled={!questionText.trim() || isSubmitting}
      >
        {isSubmitting ? t('workshop.2026.faq.ask_sending') : t('workshop.2026.faq.ask_send')}
      </button>
    </form>
  );
};

export default WorkshopFaqInlineAsk;
